import React from "react";
import { DrawNote } from "../../student/components/StudentNotes";
import { format } from "date-fns";
import { Avatar } from "@mui/material";
import { usePrimaryColor } from "../../../components/PrimaryColorContext";


export default function Recent({ customer }) {
  let recentEvents = [];
  const { students } = customer;

  // Flatten events from all students into a single timeline
  students.forEach((student) => {
    // Add Notes
    student.notes?.forEach((note) => {
      recentEvents.push({ 
        ...note, 
        type: "note", 
        studentName: student.name, 
        studentImage: student.image,
        sortDate: new Date(note.date || note.createdAt)
      });
    });

    // Add Class History
    student.classHistory?.forEach((history) => {
      recentEvents.push({ 
        ...history, 
        type: "classHistory", 
        studentName: student.name, 
        studentImage: student.image,
        sortDate: new Date(history.startDate || history.date)
      });
    });

    // Add Sales Feedback
    student.salesFeedback?.forEach((feedback) => {
      recentEvents.push({ 
        ...feedback, 
        type: "salesFeedback", 
        studentName: student.name, 
        studentImage: student.image,
        sortDate: new Date(feedback.createdAt || feedback.date)
      });
    });
  });

  // Sort by newest first
  recentEvents.sort((a, b) => b.sortDate - a.sortDate);

  return (
    <div className="timeline-container">
      {recentEvents.length > 0 ? (
        recentEvents.map((event) => (
          <RecentEvent key={event._id} event={event} />
        ))
      ) : (
        <div className="text-center py-10 text-slate-400 italic">
          No recent activity found.
        </div>
      )}
    </div>
  );
}

function RecentEvent({ event }) {
  const formattedDate = event.sortDate ? format(event.sortDate, "dd MMM yyyy • h:mm a") : "Recent";

  return (
    <article className="timeline-event">
      <div className="timeline-marker">
        <Avatar
          src={event.studentImage ? `/images/${event.studentImage}` : ""}
          alt={event.studentName}
          sx={{ 
            width: 44, 
            height: 44, 
            border: '2px solid ${primaryColor}',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}
        />
      </div>
      <div className="timeline-content-card">
        <header className="timeline-header">
          <div className="timeline-student-tag">{event.studentName}</div>
          <time className="timeline-date">{formattedDate}</time>
        </header>
        <div className="timeline-body">
          <EventBody event={event} />
        </div>
      </div>
    </article>
  );
}

function EventBody({ event }) {
  switch (event.type) {
    case "note":
      return (
        <div className="event-note">
          <span className="font-bold text-slate-900 block mb-2">New Note</span>
          <DrawNote note={event} />
        </div>
      );
    case "classHistory":
      return (
        <div className="event-class-change">
          <span className="font-bold text-slate-900">Class changed to: </span>
          <span className="text-teal-700 font-semibold">
            {event.htClass && event.htClass.name ? event.htClass.name : "Unknown Class"}
          </span>
          {event.reason && (
            <div className="bg-slate-50 p-2 mt-2 rounded border-l-4 border-slate-200 text-slate-600 italic">
              {event.reason}
            </div>
          )}
        </div>
      );
    case "salesFeedback":
      return (
        <div className="event-feedback space-y-3">
          <div className="font-bold text-slate-900">Feedback from {event.salesPerson?.name || "Sales Team"}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-teal-50/50 p-3 rounded-lg border border-teal-100">
              <span className="text-[10px] uppercase font-black text-teal-600 block mb-1">Company Notes</span>
              <div className="text-sm">{event.notes || "N/A"}</div>
            </div>
            <div className="bg-slate-50 p-3 rounded-lg border border-slate-100">
              <span className="text-[10px] uppercase font-black text-slate-400 block mb-1">Customer Reaction</span>
              <div className="text-sm">{event.salesFeedback || "N/A"}</div>
            </div>
          </div>
        </div>
      );
    default:
      return <div className="text-slate-400">Activity detected.</div>;
  }
}
